import React, { Suspense, useEffect, useState } from "react";
import { Link, Redirect, Switch } from "react-router-dom";
import { Container, Alert } from "reactstrap";
import { Ability } from "@casl/ability";
import { uniqBy, sortBy } from "lodash";
import axios from "axios";

//Redux
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

// eslint-disable-next-line
import {
  AppFooter,
  AppHeader,
  AppSidebar,
  AppSidebarFooter,
  AppSidebarForm,
  AppSidebarHeader,
  AppSidebarMinimizer,
  AppSidebarNav,
  AppBreadcrumb,
} from "@coreui/react";
// requests
import API_ENDPOINTS from "../../configs/API_ENDPOINTS";
import { routes, breadcrumbs } from "../../routes";
//i18n
import i18n from "../../i18n";
import DefaultFooter from "./DefaultFooter";
import DefaultHeader from "./DefaultHeader";
import { Translation } from "react-i18next";

import * as i18nActions from "../../redux/actions/i18nActions";
import * as usersActions from "../../redux/actions/usersActions";
import Loading from "../../utils/loading";

function translate(key) {
  return <Translation>{(t) => t(key)}</Translation>;
}

function DefaultLayout(props) {
  const [visible, setVisible] = useState(false);
  const [releaseLog, setReleaseLog] = useState([]);
  const [ability, setAbility] = useState(new Ability([]));
  const [navConfig, setNavConfig] = useState({ items: [] });
  const [isLoggedInAsAlertVisible, setIsLoggedInAsAlertVisible] = useState(true);

  useEffect(() => {
    props.getUserAbility();
    getConfig();
    // Check if logged in as another user is in local storage
    const loggedInAs = localStorage.getItem("loggedAsAnotherUser");
    if (loggedInAs === "true") {
      setIsLoggedInAsAlertVisible(true);
    } else {
      setIsLoggedInAsAlertVisible(false);
    }
  }, []);

  useEffect(() => {
    if (props.ability) {
      getReleaseNotification();
      createNavBar();
    }
  }, [props.ability]);

  async function getConfig() {
    try {
      const { data } = await axios.get(`${API_ENDPOINTS.USER_CONFIG}`);
      if (data?.language) {
        i18n.changeLanguage(data.language);
        localStorage.setItem("selected_language", data.language);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const hideAlert = async () => {
    try {
      setVisible(false);
      await axios.patch(API_ENDPOINTS.NOTIFY, {
        notificationId: releaseLog[releaseLog.length - 1]?._id,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const showAlert = () => setVisible(true);

  const createNavBar = () => {
    const userAbility = new Ability(props.ability || []);
    setAbility(userAbility);

    const menuItems = [];

    // Default menu items
    menuItems.push(
      ...[
        {
          rank: 1,
          name: translate("dashboard"),
          url: "/dashboard",
          icon: "icon-speedometer",
          badge: { variant: "info" },
        },
        {
          rank: 4,
          name: translate("organizations"),
          url: "/organizations",
          icon: "icon-layers",
          badge: { variant: "info" },
        },
        {
          rank: 6,
          name: translate("counters"),
          url: "/counters",
          icon: "fa fa-bar-chart",
          badge: { variant: "info" },
        },
        {
          rank: 7,
          name: translate("map"),
          url: "/map",
          icon: "icon-map",
        },
      ]
    );

    // Admin menu items
    if (userAbility.can("manage", "all")) {
      menuItems.push(
        ...[
          {
            rank: 2,
            name: translate("users"),
            url: "/users",
            icon: "icon-people",
            badge: { variant: "info" },
          },
          {
            rank: 3,
            name: translate("roles"),
            url: "/roles",
            icon: "icon-umbrella",
            badge: { variant: "info" },
          },
          {
            rank: 8,
            name: translate("monitoring"),
            url: "/monitoring",
            icon: "icon-bell",
            badge: { variant: "info" },
          },
        ]
      );
    }

    // Device manager menu items
    if (userAbility.can("read", "devices")) {
      menuItems.push(
        ...[
          {
            rank: 5,
            name: translate("devices"),
            url: "/devices",
            icon: "icon-grid",
            badge: { variant: "info" },
          },
        ]
      );
    }

    // Orders menu items
    if (userAbility.can("read", "services")) {
      menuItems.push(
        ...[
          {
            rank: 9,
            name: translate("orders"),
            url: "/orders",
            icon: "icon-wrench",
            badge: { variant: "info" },
          },
        ]
      );
    }

    const sortedNav = sortBy(
      uniqBy(menuItems, (nav) => nav.rank),
      (nav) => nav.rank
    );

    setNavConfig((prev) => ({
      ...prev,
      items: [...sortedNav],
    }));
  };

  async function getReleaseNotification() {
    try {
      const { data } = await axios.get(`${API_ENDPOINTS.NOTIFY}/new-release`);
      if (data.data && data.data.length > 0) {
        showAlert();
        setReleaseLog(data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }
  return (
    <div className="app">
      <React.Fragment>
        {props.ability && (
          <AppHeader fixed>
            <DefaultHeader />
          </AppHeader>
        )}
        <div className="app-body">
          {props.ability && (
            <AppSidebar fixed display="lg">
              <AppSidebarHeader />
              <AppSidebarForm />
              <AppSidebarNav navConfig={navConfig} />
              <AppSidebarFooter />
              <AppSidebarMinimizer />
            </AppSidebar>
          )}
          <main className="main">
            <Alert color="primary" className="d-flex justify-content-center " isOpen={visible} toggle={hideAlert}>
              <Link to={{ pathname: "/release" }} onClick={() => setVisible(false)}>
                <h3 className="  ">
                  <span className="">
                    {releaseLog[0]
                      ? "Release v" + releaseLog[0].notification.releaseVersion + " is out now"
                      : "New update to the platform, check it out!"}
                  </span>
                </h3>
              </Link>
            </Alert>
            <Alert color="danger" className="d-flex justify-content-center " isOpen={isLoggedInAsAlertVisible}>
              <h3 className="  ">
                <span className="">{"⚠️ sign-in as feature is in use!"}</span>
              </h3>
            </Alert>
            <AppBreadcrumb appRoutes={breadcrumbs} />
            <Container fluid>
              <Suspense fallback={<Loading />}>
                <Switch>
                  {routes}
                  <Redirect from="/" to="/dashboard" />
                </Switch>
              </Suspense>
            </Container>
          </main>
        </div>

        <AppFooter>
          <DefaultFooter />
        </AppFooter>
      </React.Fragment>
    </div>
  );
}

const mapStateToProps = (state) => ({
  ability: state.usersReducer.ability,
  token: state.authReducer.token,
  language: state.i18nReducer.language,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ ...usersActions, ...i18nActions }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(DefaultLayout);
